import UIkit from "uikit";
import { useContext, useEffect, useState, useMemo, useRef } from "react";
import { AppContexts } from "../../providers";
import { Image } from "uikit-react";
import FeatherIcon from "../common/FeatherIcon";
import GuideSectionDeleteDialog from "./section-delete-dialog";
import { useQueuedSave } from "./use-queued-save";

const DELETE_DIALOG_ID = "extras-guide-section-delete";

const BuilderExtrasSectionsPanel = ({ onSaved, onRegisterSave }) => {
  const { settings, set, get, setSettings } = useContext(
    AppContexts.SettingsContext,
  );

  const [tabs, setTabs] = useState([]);
  const [newExtrasSectionName, setNewExtrasSectionName] = useState("");
  const [sectionToDelete, setSectionToDelete] = useState(null);
  const tabsRef = useRef([]);
  const identitiesRef = useRef(new Map());
  const hydratedRef = useRef(false);
  const accessoriesRef = useRef(settings?.accessories);
  const queueSave = useQueuedSave(400);
  useEffect(() => onRegisterSave?.(queueSave), [onRegisterSave, queueSave]);

  // Load accessories from backend if not already loaded
  useEffect(() => {
    if (!settings?.accessories) {
      get(["accessories"]);
    }
  }, [settings, get]);

  // Save responses update SettingsContext before the awaiting save resumes.
  // Hydrate once so an older response cannot replace a newer local draft.
  useEffect(() => {
    if (!settings?.accessories || hydratedRef.current) return;
    hydratedRef.current = true;
    accessoriesRef.current = settings.accessories;
    const initialTabs = (settings.accessories.accessory_tabs || []).map((tab) => ({
      ...tab,
      _clientId: tab.id ? `extras-${tab.id}` : Math.random().toString(36).slice(2),
    }));
    tabsRef.current = initialTabs;
    setTabs(initialTabs);
  }, [settings?.accessories]);

  const visibleTabs = useMemo(
    () => tabs.filter((t) => !t.deleted),
    [tabs],
  );

  const updateDraft = (updatedTabs, base = accessoriesRef.current) => {
    tabsRef.current = updatedTabs;
    setTabs(updatedTabs);
    const payloadTabs = updatedTabs.map(({ _clientId, ...rest }) => rest);
    const optimisticAccessories = {
      ...(base || {}),
      tab_order: payloadTabs.filter((tab) => !tab.deleted).map((tab) => tab.id).filter(Boolean),
      accessory_tabs: payloadTabs,
    };
    accessoriesRef.current = optimisticAccessories;
    setSettings?.((current) => ({
      ...current,
      accessories: optimisticAccessories,
    }));
  };

  const saveAccessories = (updatedTabs) => {
    updateDraft(updatedTabs);
    return queueSave(async () => {
      // Resolve identities when this queued request starts, after earlier
      // creates have returned their IDs. Captured drafts can still be ID-less.
      const requestedTabs = updatedTabs
        .map((tab) => ({ ...tab, ...identitiesRef.current.get(tab._clientId) }))
        .filter((tab) => tab.id || !tab.deleted);
      const payloadTabs = requestedTabs.map(({ _clientId, ...rest }) => rest);
      const result = await set(["accessories"], {
        accessories: {
          ...accessoriesRef.current,
          tab_order: payloadTabs.filter((tab) => !tab.deleted).map((tab) => tab.id).filter(Boolean),
          accessory_tabs: payloadTabs,
        },
      });

      const savedTabs = result.accessories.accessory_tabs || [];
      const knownIds = new Set(requestedTabs.filter((tab) => tab.id).map((tab) => String(tab.id)));
      // The API returns new rows in request order; deleted existing rows are
      // omitted. Match new IDs separately so deletes cannot shift this mapping.
      const createdTabs = savedTabs.filter((tab) => !knownIds.has(String(tab.id)));
      requestedTabs.filter((tab) => !tab.id).forEach((tab, index) => {
        const saved = createdTabs[index];
        identitiesRef.current.set(tab._clientId, { id: saved.id, slug: saved.slug });
      });
      const deletedIds = new Set(updatedTabs.filter((tab) => tab.deleted).map((tab) => tab._clientId));
      const currentTabs = tabsRef.current
        .filter((tab) => !tab.deleted || !deletedIds.has(tab._clientId))
        .map((tab) => ({ ...tab, ...identitiesRef.current.get(tab._clientId) }));
      updateDraft(currentTabs, result.accessories);
      return result;
    })
      .then((result) => {
        if (result) onSaved?.();
      })
      .catch(() => {
        UIkit.notification("Unable to save Extras changes", "error");
        // Retain the draft and let the next save/flush retry it.
      });
  };

  const handleChange = (clientId, field, value) => {
    const updatedTabs = tabsRef.current.map((t) => {
      if (t._clientId !== clientId) return t;

      if (field === "label") {
        return { ...t, label: value };
      }
      if (field === "hidden") {
        return { ...t, hidden: value };
      }
      return t;
    });

    saveAccessories(updatedTabs);
  };

  const handleAddSection = () => {
    const label = (newExtrasSectionName || "").trim();
    if (!label) return;

    const newTab = {
      label,
      hidden: false,
      deleted: false,
      ordering: [],
      _clientId: Math.random().toString(36).slice(2),
    };

    const updatedTabs = [...tabsRef.current, newTab];
    saveAccessories(updatedTabs);

    setNewExtrasSectionName(""); // clear input
  };

  const handleDeleteSection = async () => {
    const clientId = sectionToDelete?._clientId;
    if (!clientId) return;

    const updatedTabs = tabsRef.current.map((t) =>
      t._clientId === clientId ? { ...t, deleted: true } : t,
    );
    await saveAccessories(updatedTabs);
    setSectionToDelete(null);
    UIkit.modal(`#${DELETE_DIALOG_ID}`).hide();
  };

  const handleReorder = () => {
    const container = document.querySelector(".extras-sections-sortable");
    if (!container) return;

    const ids = [...container.querySelectorAll("[data-id]")].map((el) =>
      el.getAttribute("data-id"),
    );

    const reordered = ids
      .map((id) => tabsRef.current.find((t) => t._clientId === id))
      .filter(Boolean);

    // Include any items not in DOM (e.g., deleted ones) at the end
    const missing = tabsRef.current.filter(
      (t) => !ids.includes(t._clientId),
    );
    const updatedTabs = [...reordered, ...missing];

    saveAccessories(updatedTabs);
  };

  // Hook up UIkit sortable "stop" event
  useEffect(() => {
    const container = document.querySelector(".extras-sections-sortable");
    if (!container) return;

    const onStop = () => handleReorder();
    container.addEventListener("stop", onStop);
    return () => container.removeEventListener("stop", onStop);
  }, [tabs]); // reattach when list changes

  return (
    <div>
      {!settings?.accessories ? (
        <div className="builder-panel-loading">
          <span data-uk-spinner="ratio: 1.2" />
          <p>Loading Extras sections…</p>
        </div>
      ) : (
        <>
      <p className="secondary uk-margin-small-bottom">
        Organize your Extras into sections, such as Early Check-In, Rentals,
        or Services. Changes save automatically and are live in the guestbook.
      </p>

      <ul
        className="extras-sections-sortable builder-section-list"
        uk-sortable="handle: .uk-sortable-handle"
        style={{ listStyle: "none", paddingLeft: 0, margin: 0 }}
      >
        {visibleTabs.map((section) => (
          <li
            key={section._clientId}
            data-id={section._clientId}
            className="builder-section-list-item"
          >
            <div className="builder-section-row">
              <div className="uk-flex uk-flex-middle uk-flex-between">
                {/* Section name + drag handle */}
                <div className="uk-flex uk-flex-middle builder-section-main">
                  <button
                    type="button"
                    className="uk-button hms-simple-icon-btn uk-sortable-handle uk-margin-small-right"
                    uk-tooltip="Drag to reorder sections"
                  >
                    <Image src="/images/move.svg" />
                  </button>

                  <input
                    className="uk-input"
                    style={{ width: "100%" }}
                    type="text"
                    value={section.label || ""}
                    onChange={(e) =>
                      handleChange(
                        section._clientId,
                        "label",
                        e.target.value,
                      )
                    }
                    placeholder="e.g., Early Check-In"
                  />
                </div>

                <div className="builder-section-actions">
                  {/* Hide toggle */}
                  <div className="uk-width-auto uk-text-center">
                    <label
                      className="setting-switch rbr-checkbox"
                      htmlFor={`extrasSectionHidden-${section._clientId}`}
                      uk-tooltip="Hide/show this Extras section in the guide"
                    >
                      <input
                        id={`extrasSectionHidden-${section._clientId}`}
                        type="checkbox"
                        aria-label={`Show ${section.label || "Extras section"}`}
                        checked={!section.hidden}
                        onChange={(e) =>
                          handleChange(
                            section._clientId,
                            "hidden",
                            !e.target.checked,
                          )
                        }
                      />
                      <span className="setting-slider setting-round"></span>
                    </label>
                  </div>

                  {/* Delete icon */}
                  <div className="uk-width-auto uk-text-center">
                    <button
                      className="uk-button hms-simple-icon-btn"
                      type="button"
                      onClick={() => setSectionToDelete(section)}
                      uk-toggle={`target: #${DELETE_DIALOG_ID}`}
                      uk-tooltip="Delete this Extras section"
                      aria-label={`Delete ${section.label || "Extras section"}`}
                    >
                      <FeatherIcon name="trash-2" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </li>
        ))}
      </ul>

      <div className="uk-width-1-1 uk-margin-top">
        <div className="add-dest-gd-tab-wrapper">
          <div className="uk-flex uk-flex-middle">
            <input
              className="uk-input"
              type="text"
              placeholder="Section Name"
              value={newExtrasSectionName}
              onChange={(e) => setNewExtrasSectionName(e.target.value)}
            />
            <div className="plus-w-2">
              <button
                className="uk-button hms-btn hms-gray-btn btn-no-margin"
                type="button"
                onClick={handleAddSection}
              >
                ADD
              </button>
            </div>
          </div>
        </div>
      </div>
      </>
      )}
      <GuideSectionDeleteDialog
        id={DELETE_DIALOG_ID}
        onCancel={() => setSectionToDelete(null)}
        onDelete={handleDeleteSection}
      />
    </div>
  );
};

export default BuilderExtrasSectionsPanel;
